import { useCallback, useState } from 'react';

import { getLocalStorage, removeLocalStorage, setLocalStorage } from '@/utils/localstorage';
import { useLoggedInUser } from '@/utils/logged-in-user-provider';

import { OnboardingData } from './types';

const STORAGE_KEY = 'wac.loom-onboarding.data';
// Onboarding is short-lived, but we want to survive page reloads and brief
// drop-offs. 7 days is comfortably longer than any realistic flow duration.
const STORAGE_TTL_SECONDS = 60 * 60 * 24 * 7;

/**
 * Build the default onboarding data. `role` is intentionally left empty
 * (undefined) until the user selects one during the flow.
 */
export const getDefaultOnboardingData = (aaid: string): OnboardingData => ({
	aaid,
	useCase: 'work',
	role: undefined,
	recorder: 'none',
});

const readFromStorage = (): OnboardingData | undefined => {
	const raw = getLocalStorage(STORAGE_KEY, true);
	if (!raw) {
		return undefined;
	}
	try {
		return JSON.parse(raw) as OnboardingData;
	} catch (error) {
		console.warn('Failed to read loom onboarding data from localStorage', error);
		return undefined;
	}
};

const writeToStorage = (data: OnboardingData): void => {
	try {
		setLocalStorage(STORAGE_KEY, JSON.stringify(data), STORAGE_TTL_SECONDS);
	} catch (error) {
		console.warn('Failed to save loom onboarding data to localStorage', error);
	}
};

const clearStorage = (): void => {
	try {
		removeLocalStorage(STORAGE_KEY);
	} catch (error) {
		console.warn('Failed to clear loom onboarding data from localStorage', error);
	}
};

/**
 * Seed a fresh onboarding record in localStorage. Call this once when the
 * flow starts so we overwrite any cached data left behind by a previous
 * user on the same browser.
 */
export const createOnboardingData = (aaid: string): OnboardingData => {
	const fresh = getDefaultOnboardingData(aaid);
	writeToStorage(fresh);
	return fresh;
};

export const useOnboardingData = () => {
	const { accountId = '' } = useLoggedInUser();

	// function to store the data as a state with default/empty fields.
	// Hydrates from localStorage on first render so a page reload mid-flow
	// keeps the user's progress.
	const [data, setData] = useState<OnboardingData>(
		() => readFromStorage() ?? getDefaultOnboardingData(accountId),
	);

	// funtion to update the data we have saved
	const updateOnboardingData = useCallback((updates: Partial<OnboardingData>) => {
		setData((prev) => {
			const next = { ...prev, ...updates };
			writeToStorage(next);
			return next;
		});
	}, []);

	const clearOnboardingData = useCallback(() => {
		clearStorage();
		return data;
	}, [data]);

	return { data, updateOnboardingData, clearOnboardingData };
};
